import { useEffect } from 'react'
import { useController, useFormContext } from 'react-hook-form'
import { useIsFetching, useIsMutating } from 'react-query'
import { Box, Button, CircularProgress } from '@mui/material'

import { DefaultElementProps } from 'common/interfaces'
import { mainButtonStyle } from 'common/styles'
import { SizingContainer } from 'components/SizingContainer'
import InlineBackButton from 'components/StepContainer/InlineBackButton'
import { useFlow } from 'utils/flow'
import { useBoolean, useIsOnline, useNext } from 'utils/hook'

import { NextButton as NextButtonProps } from './interface'

const liveRegionSx = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  borderWidth: 0,
}

const NextButton = ({
  name,
  text = 'Continue',
  variant = 'contained',
  onClick,
}: NextButtonProps & DefaultElementProps): JSX.Element => {
  const isOnline = useIsOnline()

  const fetchingNumber = useIsFetching()
  const mutatingNumber = useIsMutating()

  const [isClicked, setClicked, setUnclicked] = useBoolean(false)

  const { control, trigger } = useFormContext()
  const { field } = useController({ name, control })
  const { data: flow } = useFlow()
  const inlineBack = flow?.back_button_inline === true

  const next = useNext()

  const handleClick = (): void => {
    void trigger().then((isValid) => {
      if (!isValid) {
        return
      }

      onClick?.(
        {},
        {
          onError: (e) => {
            console.log(e.message)
          },
        }
      )

      setClicked()
    })
  }

  const goToNextPage = (): void => {
    setUnclicked()
    next()
  }

  useEffect(() => {
    if (isClicked && fetchingNumber === 0 && mutatingNumber === 0) {
      goToNextPage()
    }

    if (isClicked && !isOnline) {
      goToNextPage()
    }
  }, [fetchingNumber, mutatingNumber, isClicked, isOnline])

  return (
    <SizingContainer className="next-btn-wrap">
      <Box sx={inlineBack ? mainButtonStyle.inlineContainer : mainButtonStyle.container}>
        <Box role="status" aria-live="polite" aria-atomic="true" sx={liveRegionSx}>
          {isClicked ? 'Loading next step' : ''}
        </Box>
        <InlineBackButton />
        <Button
          {...field}
          type="button"
          onClick={handleClick}
          variant={variant}
          sx={{
            ...(inlineBack ? mainButtonStyle.inlineButton : mainButtonStyle.button),
            position: 'relative',
            display: 'inline-flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
          disabled={isClicked}
          aria-busy={isClicked}
        >
          {isClicked && (
            <CircularProgress
              aria-hidden
              size={30}
              sx={{
                position: 'absolute',
                color: 'primary.dark',
              }}
            />
          )}
          {text}
        </Button>
      </Box>
    </SizingContainer>
  )
}

export default NextButton
